import { get, download } from './apiClient';

// Serviço de analytics
export const analyticsService = {
  // Util: formata data para 'YYYY-MM-DD' no fuso de São Paulo
  _formatApiDate(d) {
    if (!d) return undefined;
    return new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  },
  // Buscar produtos com servidor (search + paginação)
  async searchProducts({ q = '', offset = 0, limit = 50 } = {}) {
    try {
      const params = { q, offset, limit };
      const response = await get('/api/v1/analytics/products/list', params);
      const list = response?.data || [];
      // Normalizar para AsyncSearchSelect
      return list.map(p => ({ id: p.id || p.product_id || p?.id, label: p.title || p.product_title || 'Produto' }));
    } catch (error) {
      console.error('Erro ao buscar produtos (analytics):', error);
      return [];
    }
  },

  // Obter analytics completos do dashboard
  async getDashboardAnalytics(filters = {}) {
    const params = {};
    
    // Adicionar filtros como parâmetros de query
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }
    if (filters.productId) {
      params.product_id = filters.productId;
    }
    if (filters.utmSource) {
      params.utm_source = filters.utmSource;
    }
    if (filters.utmCampaign) {
      params.utm_campaign = filters.utmCampaign;
    }
    if (filters.utmMedium) {
      params.utm_medium = filters.utmMedium;
    }
    if (filters.stateCode) {
      params.state_code = filters.stateCode;
    }
    if (filters.orderStatus) {
      params.order_status = filters.orderStatus;
    }

    const response = await get('/api/v1/analytics/dashboard', params);
    return response;
  },

  // Obter análise de origem de tráfego
  async getTrafficSources(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/api/v1/analytics/traffic-sources', params);
    return response;
  },

  // Obter análise geográfica
  async getGeographicAnalytics(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/api/v1/analytics/geographic', params);
    return response;
  },

  // Obter padrões de venda por horário
  async getHourlyPatterns(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/api/v1/analytics/hourly-patterns', params);
    return response;
  },

  // =====================================
  // Inventory Analytics
  // =====================================
  
  // Obter valor por status do inventário (não-entregues)
  async getInventoryValueByStatus(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }
    if (filters.category) {
      params.category = filters.category;
    }
    if (filters.productId) {
      params.product_id = filters.productId;
    }

    const response = await get('/finance/analytics/inventory/value-by-status', params);
    return response;
  },

  // Obter lead time por status (timeline)
  async getInventoryLeadTime(filters = {}) {
    const params = {
      granularity: filters.granularity || 'week'
    };
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/finance/analytics/inventory/lead-time', params);
    return response;
  },

  // Obter scatter "dias em preparação" vs "valor final"
  async getInventoryPreparationScatter(filters = {}) {
    const params = {
      limit: filters.limit || 500,
      include_delivered: filters.includeDelivered !== undefined ? filters.includeDelivered : true
    };

    const response = await get('/finance/analytics/inventory/preparation-scatter', params);
    return response;
  },

  // =====================================
  // Finance Analytics
  // =====================================
  
  // Obter evolução mensal por classificação
  async getFinanceMonthlyEvolution(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/finance/analytics/finance/monthly-evolution', params);
    return response;
  },

  // Obter margem por produto (ranking)
  async getFinanceProductMargin(filters = {}) {
    const params = {
      limit: filters.limit || 20
    };
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/finance/analytics/finance/product-margin', params);
    return response;
  },

  // Obter KPIs de verificação
  async getFinanceVerificationKPIs(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/finance/analytics/finance/verification-kpis', params);
    return response;
  },

  // Obter análise de produtos
  async getProductAnalytics(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/api/v1/analytics/products', params);
    return response;
  },

  // Obter resumo executivo
  async getAnalyticsSummary(filters = {}) {
    const params = {};
    
    if (filters.startDate) {
      params.start_date = this._formatApiDate(filters.startDate);
    }
    if (filters.endDate) {
      params.end_date = this._formatApiDate(filters.endDate);
    }

    const response = await get('/api/v1/analytics/summary', params);
    return response;
  },

  // Gerar e baixar relatório PDF
  async downloadReport(filters = {}, title = 'Relatório BI') {
    const params = {
      title,
      include_charts: true,
      include_tables: true,
      format: 'pdf',
      filters: JSON.stringify(filters),
    };

    // Construir query string para download
    const queryString = new URLSearchParams(params).toString();
    const filename = `relatorio-bi-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}.pdf`;
    
    const response = await download(`/api/v1/reports/generate?${queryString}`, filename);
    return response;
  },

  // Formatar dados para gráficos
  formatChartData(data, type = 'line') {
    switch (type) {
      case 'line':
        return {
          labels: data.map(item => item.label || item.date || item.time_label),
          datasets: [{
            label: 'Vendas',
            data: data.map(item => item.total_sales || item.value),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1,
          }],
        };

      case 'bar':
        return {
          labels: data.map(item => item.label || item.name || item.state_name),
          datasets: [{
            label: 'Receita',
            data: data.map(item => parseFloat(item.total_revenue || item.revenue || 0)),
            backgroundColor: [
              'rgba(255, 99, 132, 0.8)',
              'rgba(54, 162, 235, 0.8)',
              'rgba(255, 205, 86, 0.8)',
              'rgba(75, 192, 192, 0.8)',
              'rgba(153, 102, 255, 0.8)',
              'rgba(255, 159, 64, 0.8)',
            ],
            borderColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 205, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 159, 64, 1)',
            ],
            borderWidth: 1,
          }],
        };

      case 'pie':
        return {
          labels: data.map(item => item.label || item.name || item.utm_source),
          datasets: [{
            data: data.map(item => parseFloat(item.percentage_share || item.total_revenue || 0)),
            backgroundColor: [
              '#FF6384',
              '#36A2EB',
              '#FFCE56',
              '#4BC0C0',
              '#9966FF',
              '#FF9F40',
              '#FF6384',
              '#C9CBCF',
              '#4BC0C0',
              '#FF6384',
            ],
            hoverBackgroundColor: [
              '#FF6384',
              '#36A2EB',
              '#FFCE56',
              '#4BC0C0',
              '#9966FF',
              '#FF9F40',
              '#FF6384',
              '#C9CBCF',
              '#4BC0C0',
              '#FF6384',
            ],
          }],
        };

      default:
        return data;
    }
  },

  // Formatar valores monetários
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  },

  // Formatar números
  formatNumber(value) {
    return new Intl.NumberFormat('pt-BR').format(value || 0);
  },

  // Formatar porcentagem
  formatPercentage(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format((value || 0) / 100);
  },

  // Calcular variação percentual
  calculatePercentageChange(current, previous) {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  },

  // Obter período padrão (últimos 30 dias)
  getDefaultDateRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    
    return {
      startDate,
      endDate,
    };
  },
};
